import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, FileText, ClipboardList, BarChart } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { logoutThunk } from "../store/redux/thunk";
import { fetchOrganizationThunk } from "../store/redux/thunk";
import { fetchScreenDataThunk } from "../store/screen/thunk";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Select from "react-select";
import noDataImage from "../assets/no_data.png";

const iconMap = {
  FileText: <FileText />,
  ClipboardList: <ClipboardList />,
  BarChart: <BarChart />,
};

const selectStyles = `
  .react-select-container {
    width: 100%;
  }

  .react-select__control {
    min-height: 38px !important;
    border: 1px solid #ccc !important;
    border-radius: 4px !important;
    background-color: white !important;
    font-family: inherit !important;
    font-size: inherit !important;
  }

  .react-select__control:hover {
    border-color: #aaa !important;
  }

  .react-select__value-container {
    padding: 2px 8px !important;
    text-align: left !important;
  }

  .react-select__single-value {
    text-align: left !important;
    font-family: inherit !important;
    font-size: inherit !important;
  }

  .react-select__input-container {
    margin: 0 !important;
    padding: 0 !important;
    text-align: left !important;
  }

  .react-select__input {
    font-family: inherit !important;
    font-size: inherit !important;
  }

  .react-select__placeholder {
    text-align: left !important;
    font-family: inherit !important;
    font-size: 10px !important;
  }

  .react-select__indicators {
    height: 36px !important;
  }

  .react-select__menu {
    margin-top: 2px !important;
    border-radius: 4px !important;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
    text-align: left !important;
  }

  .react-select__option {
    padding: 8px 12px !important;
    text-align: left !important;
    font-family: inherit !important;
    font-size: inherit !important;
  }

  .react-select__control--is-focused {
    box-shadow: none !important;
    border-color: #66afe9 !important;
  }

  .react-select__menu-list {
    max-height: 200px !important;
  }

  .react-select__input-container input {
    font-family: inherit !important;
    font-size: inherit !important;
  }
`;

const MenuScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // FIX 1: Safe organizations selector - always returns array
  const organizations = useSelector((state) => {
    const orgs = state.filter?.organizations;
    console.log("organizations =>", orgs);
    console.log("isArray =>", Array.isArray(orgs));
    return Array.isArray(orgs) ? orgs : [];
  });
  
  const { screens, loading, error } = useSelector((state) => state.screens);

  const [selectedOrg, setSelectedOrg] = useState("");
  const [showModal, setShowModal] = useState(true);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const restoreSession = async () => {
      const savedOrg = await AsyncStorage.getItem("selectedOrg");
      const modalState = await AsyncStorage.getItem("showModal");

      if (savedOrg) {
        setSelectedOrg(savedOrg);
      }

      if (modalState === "false") {
        setShowModal(false);
      }
    };

    restoreSession();
  }, []);

  useEffect(() => {
    const fetchUserId = async () => {
      const storedUserId = await AsyncStorage.getItem("userId");
      setUserId(storedUserId);
      console.log("User ID:", storedUserId);
    };
    fetchUserId();
  }, []);

  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.textContent = selectStyles;
    document.head.appendChild(styleTag);
    
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  useEffect(() => {
    if (userId) {
      dispatch(fetchOrganizationThunk(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (selectedOrg) {
      dispatch(fetchScreenDataThunk(selectedOrg));
    }
  }, [dispatch, selectedOrg]);

  useEffect(() => {
    const checkLogin = async () => {
      const storedUserId = await AsyncStorage.getItem("userId");
      if (!storedUserId) {
        navigate("/", { replace: true });
      }
    };
    checkLogin();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await AsyncStorage.multiRemove([
        "userId", 
        "token", 
        "userData", 
        "sessionData", 
        "selectedOrg",
        "showModal"
      ]);
      
      await dispatch(logoutThunk(navigate));
      
      sessionStorage.clear();
      
      navigate("/", { replace: true });
      
      window.location.reload();
      
    } catch (error) {
      console.error("Logout error:", error);
      await AsyncStorage.clear();
      sessionStorage.clear();
      navigate("/", { replace: true });
      window.location.reload();
    }
  };

  const handleApplyFilters = async () => {
    if (selectedOrg) {
      await AsyncStorage.setItem("selectedOrg", selectedOrg);
      await AsyncStorage.setItem("showModal", "false");
      
      setShowModal(false);
    } else {
      alert("Please select an Organization.");
    }
  };

  const handleSelectScreen = (screen) => {
    navigate("/screenreports", {
      state: { screenId: screen.id, screenName: screen._identifier },
    });
  };

  // FIX 2: Safe organization options with array check
  const organizationOptions = [
    { value: "", label: "All Organizations" },
    ...(organizations.length > 0 
      ? organizations.map((org) => ({
          value: org.Ad_Org_ID,
          label: org.Organization,
        }))
      : []),
  ];

  // FIX 3: Safe selected option with array check
  const selectedOption = selectedOrg && organizations.length > 0
    ? {
        value: selectedOrg,
        label: organizations.find((org) => org.Ad_Org_ID === selectedOrg)?.Organization || "",
      }
    : { value: "", label: "All Organizations" };

  return (
    <div className="menu-container">
      <button className="logout-button" onClick={handleLogout}>
        <LogOut className="logout-icon" /> Logout
      </button>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Apply Filters</h2>
            <div className="filter-group">
              <label htmlFor="organization">Select Organization:</label>
              <Select
                id="organization"
                options={organizationOptions}
                value={selectedOption}
                onChange={(selectedOption) => setSelectedOrg(selectedOption?.value || "")}
                isSearchable={true}
                placeholder="Select Organization"
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    textAlign: 'left'
                  }),
                  menu: (base) => ({
                    ...base,
                    textAlign: 'left'
                  }),
                  option: (base) => ({
                    ...base,
                    textAlign: 'left'
                  }),
                  singleValue: (base) => ({
                    ...base,
                    textAlign: 'left'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    textAlign: 'left'
                  }),
                  input: (base) => ({
                    ...base,
                    textAlign: 'left'
                  })
                }}
              />
            </div>
            <div className="modal-actions">
              <button onClick={handleApplyFilters} className="apply-button">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div id="reports">
        <h2 className="menu-title">Reports</h2>
        {loading && <p>Loading menu...</p>}
        {error && <p className="error">{error}</p>}

        {!loading && !error && (
          <>
            {screens && screens.length > 0 ? (
              <div className="report-list">
                {screens.map((item) => (
                  <div
                    key={item.id}
                    className="report-card"
                    onClick={() => handleSelectScreen(item)}
                  >
                    <div className="report-icon">
                      {iconMap[item.icon] || <FileText />}
                    </div>
                    <span className="report-name">{item._identifier}</span>
                  </div>
                ))}
              </div>
            ) : (
              !showModal && (
                <div className="no-data">
                  <img
                    src={noDataImage}
                    alt="No data available"
                    className="no-data-image"
                  />
                  <p>No data available for the current date.</p>
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MenuScreen;